import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/imageUrl";
import { ArrowRight, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";

const FALLBACK_HERO =
  "https://images.unsplash.com/photo-1558963235-eff20c9f7d99?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
const ROTATE_MS = 5000;

export default function HeroCarousel() {
  const { t, lang } = useApp();
  const [slides, setSlides] = useState(null); // null = loading, [] = use fallback
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setSlides(r.data?.banner || []))
      .catch(() => setSlides([]));
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (!slides || slides.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [slides]);

  const wa = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hello, I'm interested in your rice products."
  )}`;

  const next = () => slides && setIdx((i) => (i + 1) % slides.length);
  const prev = () => slides && setIdx((i) => (i - 1 + slides.length) % slides.length);

  const usingFallback = !slides || slides.length === 0;
  const currentSlide = usingFallback ? null : slides[idx];
  const bgUrl = currentSlide ? resolveImageUrl(currentSlide.image_url) : FALLBACK_HERO;

  // Per-slide overlay title (fallback to default branding)
  const overlayTitle = currentSlide
    ? lang === "en"
      ? currentSlide.title_en || t.home.heroTitle
      : currentSlide.title_te || t.home.heroTitle
    : t.home.heroTitle;

  return (
    <section
      data-testid="hero-carousel"
      className="relative min-h-[88vh] flex items-end overflow-hidden"
    >
      {/* Slides — crossfade transition */}
      {usingFallback ? (
        <img src={FALLBACK_HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        slides.map((s, i) => (
          <img
            key={s.id}
            src={resolveImageUrl(s.image_url)}
            alt=""
            data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              i === idx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />

      {/* Foreground content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-32 w-full">
        <div className="max-w-3xl">
          <div className="inline-block text-[11px] tracking-[0.25em] uppercase text-[#e69138] font-semibold mb-5 border border-[#e69138]/40 px-3 py-1 rounded-sm">
            {t.home.tag}
          </div>
          <h1
            key={overlayTitle /* re-animate on slide change */}
            data-testid="hero-overlay-title"
            className={`text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold animate-fadein ${
              lang === "te" ? "font-telugu" : ""
            }`}
          >
            {overlayTitle}
          </h1>
          <p
            className={`text-stone-200 mt-6 text-base sm:text-lg max-w-2xl leading-relaxed ${
              lang === "te" ? "font-telugu" : ""
            }`}
          >
            {t.home.heroSub}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              data-testid="hero-shop-cta"
              to="/products"
              className="inline-flex items-center justify-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white px-7 py-3.5 rounded-sm font-semibold text-sm tracking-wide transition-colors"
            >
              {t.home.cta1} <ArrowRight size={16} />
            </Link>
            <a
              data-testid="hero-wa-cta"
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white/95 hover:bg-white text-stone-900 px-7 py-3.5 rounded-sm font-semibold text-sm tracking-wide transition-colors"
            >
              <MessageCircle size={16} /> {t.home.cta2}
            </a>
          </div>
        </div>
      </div>

      {/* Carousel controls — only if 2+ slides */}
      {!usingFallback && slides.length > 1 && (
        <>
          <button
            data-testid="hero-prev"
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 grid place-items-center rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="hero-next"
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 grid place-items-center rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[#e69138]" : "w-2 bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
